import React, { useState } from 'react';
import { Sparkles, LogIn, Send, Lock } from 'lucide-react';

export const ReferAndEarnSection = ({
  onOpenInquiry,
  onNavigateToSignIn,
}) => {
  const [loanDisbursementVolume, setLoanDisbursementVolume] = useState(3000000); // 30 Lakhs
  const [insuranceMonthlyPremium, setInsuranceMonthlyPremium] = useState(100000); // 1 Lakh

  // Commission calculations:
  // Loans: 2.2%
  // Insurance: 35%
  const loanCommission = Math.round(loanDisbursementVolume * 0.022);
  const insuranceCommission = Math.round(insuranceMonthlyPremium * 0.35);
  const totalCommission = loanCommission + insuranceCommission;

  return (
    <section id="refer-earn-section" className="py-16 bg-slate-50 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Clean Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold uppercase tracking-wider mb-2 border border-amber-200">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
            <span>High-Yield Partnership Model</span>
          </div>

          <h2 className="text-2xl sm:text-4xl font-black text-[#0c2e6b] tracking-tight">
            Become a Partner: Refer &amp; Earn
          </h2>

          <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-2xl mx-auto">
            Refer clients seeking business/personal loans or IRDA-licensed insurance policies, and earn attractive commissions credited directly to your bank account via instant IMPS.
          </p>
        </div>

        {/* 3 Simple Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 text-center shadow-xs">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-extrabold text-base flex items-center justify-center mx-auto mb-3">
              1
            </div>
            <h3 className="font-bold text-base text-[#0c2e6b] mb-1">Register for Free</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              No upfront investment. Provide your basic KYC and bank details for automated payout credits.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 text-center shadow-xs">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-extrabold text-base flex items-center justify-center mx-auto mb-3">
              2
            </div>
            <h3 className="font-bold text-base text-[#0c2e6b] mb-1">Refer Borrowers &amp; Clients</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Introduce business owners needing loans or families needing health, term, or motor insurance.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 text-center shadow-xs">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-extrabold text-base flex items-center justify-center mx-auto mb-3">
              3
            </div>
            <h3 className="font-bold text-base text-[#0c2e6b] mb-1">Earn Direct Commissions</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Receive up to 2.5% on loan disbursements and up to 40% on insurance policies via instant bank IMPS.
            </p>
          </div>
        </div>

        {/* Clean, Simple Commission Estimator */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 max-w-4xl mx-auto shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 space-y-6">
              <h3 className="text-lg font-bold text-[#0c2e6b]">
                Monthly Referral Earnings Calculator
              </h3>

              {/* Loan Slider */}
              <div>
                <div className="flex justify-between text-xs mb-1.5 font-semibold text-slate-700">
                  <span>Monthly Loan Disbursed via Referrals:</span>
                  <span className="font-mono text-blue-700 font-bold">
                    ₹{(loanDisbursementVolume / 100000).toFixed(1)} Lakhs
                  </span>
                </div>
                <input
                  type="range"
                  min="200000"
                  max="10000000"
                  step="100000"
                  value={loanDisbursementVolume}
                  onChange={(e) => setLoanDisbursementVolume(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="text-[11px] text-slate-400 mt-1">
                  * Average 2.2% direct DSA commission on personal and business loans.
                </div>
              </div>

              {/* Insurance Slider */}
              <div>
                <div className="flex justify-between text-xs mb-1.5 font-semibold text-slate-700">
                  <span>Monthly Insurance Premium Referred:</span>
                  <span className="font-mono text-emerald-700 font-bold">
                    ₹{(insuranceMonthlyPremium / 1000).toFixed(0)} Thousand
                  </span>
                </div>
                <input
                  type="range"
                  min="20000"
                  max="1000000"
                  step="10000"
                  value={insuranceMonthlyPremium}
                  onChange={(e) => setInsuranceMonthlyPremium(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                />
                <div className="text-[11px] text-slate-400 mt-1">
                  * Up to 35% on first-year premiums for IRDA health and term policies.
                </div>
              </div>
            </div>

            {/* Total Display */}
            <div className="md:col-span-5 bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-700 block">
                Estimated Monthly Income
              </span>
              <div className="text-3xl font-extrabold text-[#0c2e6b] font-mono my-2">
                ₹{totalCommission.toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-slate-600 mb-5 leading-relaxed">
                Disbursed directly into your merchant bank account by Fi Pay Bills.
              </p>

              <button
                onClick={() => onOpenInquiry('Become a Partner (Refer & Earn)')}
                className="w-full py-3 px-4 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                <span>Apply as a Partner</span>
              </button>
            </div>
          </div>
        </div>

        {/* SIGN-IN OPTION (Replaces Partner Referral Link as instructed by user) */}
        <div className="max-w-4xl mx-auto p-5 rounded-2xl bg-gradient-to-r from-blue-900 to-[#0c2e6b] text-white flex flex-col sm:flex-row items-center justify-between gap-4 shadow-md border border-blue-700">
          <div className="flex items-center gap-3.5 text-center sm:text-left">
            <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
              <Lock className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <span className="font-black text-sm sm:text-base">
                  Merchant &amp; Partner Portal
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold border border-emerald-400/30">
                  Secure Access
                </span>
              </div>
              <p className="text-xs text-blue-200 mt-0.5">
                Already registered? Sign in to view real-time commission statements, loan approval statuses, and payout ledgers.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
            {onNavigateToSignIn ? (
              <button
                onClick={onNavigateToSignIn}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-white hover:bg-blue-50 text-blue-950 font-black text-xs uppercase tracking-wider transition-colors shadow-sm cursor-pointer flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4 text-blue-700" />
                <span>Sign In to Portal</span>
              </button>
            ) : (
              <button
                onClick={() => onOpenInquiry('Partner Portal Sign In')}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-white hover:bg-blue-50 text-blue-950 font-black text-xs uppercase tracking-wider transition-colors shadow-sm cursor-pointer flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4 text-blue-700" />
                <span>Sign In</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </section>
  );
};
